import { Component, signal, HostListener } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  template: `
    <header [class.scrolled]="isScrolled()">
      <nav class="container">
        <a routerLink="/home" class="logo">
          <img src="assets/images/logo/logo-text-no-bg.png" alt="SAI" class="logo-img" width="120" height="32" />
        </a>

        <ul class="nav-links">
          <li><a routerLink="/home" routerLinkActive="active" [routerLinkActiveOptions]="{exact: true}">Home</a></li>
          <li><a routerLink="/about" routerLinkActive="active">About</a></li>
          <li><a routerLink="/agents" routerLinkActive="active">Agentic AI</a></li>
          <li><a routerLink="/contact" routerLinkActive="active">Contact</a></li>
        </ul>

        <div class="nav-actions">
            <a routerLink="/overview" class="cta-btn">
            Go To Dashboard
            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
        </div>

        <button class="mobile-toggle" (click)="toggleMobile()" [attr.aria-label]="'Toggle menu'">
          <span [class.open]="mobileOpen()"></span>
          <span [class.open]="mobileOpen()"></span>
          <span [class.open]="mobileOpen()"></span>
        </button>
      </nav>

      @if (mobileOpen()) {
        <div class="mobile-menu">
          <a routerLink="/home" (click)="closeMobile()" routerLinkActive="active" [routerLinkActiveOptions]="{exact: true}">Home</a>
          <a routerLink="/about" (click)="closeMobile()" routerLinkActive="active">About</a>
          <a routerLink="/agents" (click)="closeMobile()" routerLinkActive="active">SAI Agents</a>
          <a routerLink="/contact" (click)="closeMobile()" routerLinkActive="active">Contact</a>
          <a routerLink="/overview" (click)="closeMobile()" class="cta-btn mobile-cta">Go To Dashboard</a>
        </div>
      }
    </header>
  `,
  styles: `
    header {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1000;
      padding: 16px 0;
      transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

      &.scrolled {
        background: rgba(255, 255, 255, 0.9);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        border-bottom: 1px solid var(--border-card);
        padding: 10px 0;
        box-shadow: 0 1px 8px rgba(0, 0, 0, 0.04);
      }
    }

    nav {
      display: flex;
      align-items: center;
      justify-content: space-between;
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 24px;
    }

    .logo {
      display: flex;
      align-items: center;
      text-decoration: none;
      z-index: 10;
    }

    .logo-img {
      height: 32px;
      width: auto;
      max-width: 140px;
      object-fit: contain;
      display: block;
    }

    .nav-links {
      display: flex;
      list-style: none;
      gap: 8px;

      a {
        padding: 8px 16px;
        color: var(--text-secondary);
        font-size: 0.95rem;
        font-weight: 500;
        border-radius: var(--radius-sm);
        transition: all 0.3s ease;

        &:hover {
          color: var(--text-primary);
          background: var(--bg-secondary);
        }

        &.active {
          color: var(--accent-blue);
          background: rgba(57, 158, 244, 0.06);
        }
      }
    }

    .nav-actions { display: flex; align-items: center; gap: 12px; }

    .cta-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 24px;
      background: var(--gradient-accent);
      color: white !important;
      font-weight: 600;
      font-size: 0.9rem;
      border-radius: var(--radius-md);
      transition: all 0.3s ease;
      box-shadow: 0 2px 10px rgba(57, 158, 244, 0.2);

      &:hover {
        box-shadow: 0 4px 20px rgba(57, 158, 244, 0.3);
        transform: translateY(-1px);
      }
    }

    .mobile-toggle {
      display: none;
      flex-direction: column;
      gap: 5px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      z-index: 10;

      span {
        display: block;
        width: 24px;
        height: 2px;
        background: var(--text-primary);
        border-radius: 2px;
        transition: all 0.3s ease;

        &.open:nth-child(1) { transform: rotate(45deg) translate(5px, 5px); }
        &.open:nth-child(2) { opacity: 0; }
        &.open:nth-child(3) { transform: rotate(-45deg) translate(5px, -5px); }
      }
    }

    .mobile-menu {
      display: none;
      flex-direction: column;
      padding: 20px 24px 30px;
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(16px);
      border-top: 1px solid var(--border-card);
      animation: slideDown 0.3s ease;

      a {
        padding: 14px 0;
        color: var(--text-secondary);
        font-size: 1.05rem;
        font-weight: 500;
        border-bottom: 1px solid var(--border-card);
        transition: color 0.3s ease;

        &:hover, &.active { color: var(--accent-blue); }
      }

      .mobile-cta {
        margin-top: 16px;
        text-align: center;
        padding: 14px;
        border: none;
        border-radius: var(--radius-md);
      }
    }

    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @media (max-width: 768px) {
      .nav-links, .nav-actions { display: none; }
      .mobile-toggle { display: flex; }
      .mobile-menu { display: flex; }
    }
  `,
})
export class HeaderComponent {
  isScrolled = signal(false);
  mobileOpen = signal(false);

  @HostListener('window:scroll')
  onScroll() {
    this.isScrolled.set(window.scrollY > 50);
  }

  toggleMobile() {
    this.mobileOpen.update(v => !v);
  }

  closeMobile() {
    this.mobileOpen.set(false);
  }
}
